import React, { useState } from 'react';
import {
  SimpleGrid,
  Box,
  Text,
  Spinner,
  Center,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Image,
  VStack,
  HStack,
  Button
} from '@chakra-ui/react';
import { FaDownload } from 'react-icons/fa';
import { MediaCard } from './MediaCard';
import { useDownloadWithDisclaimer } from './DownloadDisclaimerModal';

export const MediaGrid = ({ media, loading, error }) => {
  const [previewMedia, setPreviewMedia] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const { requestDownload, DisclaimerModal } = useDownloadWithDisclaimer();

  const handlePreview = (item) => {
    setPreviewMedia(item);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setPreviewMedia(null);
  };

  const handleDownload = () => {
    if (previewMedia) {
      requestDownload(previewMedia.id);
    }
  };

  return (
    <Box px={{ base: 4, md: 8 }} py={8} maxW="1400px" mx="auto" w="100%" minH="400px">
      {loading ? (
        <Center py={12}>
          <Spinner size="xl" color="brand.ltRed" thickness="3px" />
        </Center>
      ) : error ? (
        <Center py={12}>
          <Text color="red.500">{error}</Text>
        </Center>
      ) : !media || media.length === 0 ? (
        <Center py={12}>
          <Text color="brand.dkGray2" fontSize="lg">
            No media found. Try adjusting your filters.
          </Text>
        </Center>
      ) : (
        <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={6}>
          {media.map((item) => (
            <MediaCard key={item.id} media={item} onPreview={handlePreview} onDownload={requestDownload} />
          ))}
        </SimpleGrid>
      )}

      <Modal isOpen={isOpen} onClose={handleClose} isCentered>
        <ModalOverlay bg="blackAlpha.800" onClick={handleClose} />
        <ModalContent bg="transparent" boxShadow="none" maxW="95vw" w="auto">
          <ModalCloseButton
            zIndex={10}
            bg="blackAlpha.700"
            color="white"
            borderRadius="full"
            _hover={{ bg: 'blackAlpha.900' }}
            size="lg"
            top={-3}
            right={-3}
          />
          <ModalBody p={0}>
            <VStack spacing={0}>
              {previewMedia?.fileType === 'pdf' ? (
                <iframe
                  src={previewMedia?.downloadPath || previewMedia?.filePath}
                  title={previewMedia?.title}
                  style={{
                    width: '95vw',
                    maxWidth: '900px',
                    height: '75vh',
                    border: 'none',
                    borderRadius: '8px 8px 0 0',
                    backgroundColor: '#fff'
                  }}
                />
              ) : previewMedia?.mediaType === 'video' ? (
                <video
                  src={previewMedia?.downloadPath || previewMedia?.filePath}
                  controls
                  autoPlay
                  style={{
                    maxWidth: '95vw',
                    maxHeight: '75vh',
                    borderRadius: '8px 8px 0 0',
                    backgroundColor: '#000'
                  }}
                />
              ) : (
                <Image
                  src={previewMedia?.downloadPath || previewMedia?.filePath}
                  alt={previewMedia?.title}
                  maxW="95vw"
                  maxH="75vh"
                  objectFit="contain"
                  borderRadius="md md 0 0"
                />
              )}
              <Box bg="brand.dkBlue" borderRadius="0 0 md md" py={3} px={4} w="100%">
                <HStack justify="space-between" align="center">
                  <VStack align="flex-start" spacing={0} flex={1}>
                    <Text fontWeight="600" color="white" fontSize="md" noOfLines={1}>
                      {previewMedia?.title}
                    </Text>
                    <HStack fontSize="sm" color="whiteAlpha.800" spacing={4}>
                      <Text>{previewMedia?.fileType?.toUpperCase()}</Text>
                      {previewMedia?.width && previewMedia?.height && (
                        <Text>{(previewMedia.dimensionUnit || 'px') === 'in' ? `${previewMedia.width}" x ${previewMedia.height}"` : `${previewMedia.width} x ${previewMedia.height}px`}</Text>
                      )}
                    </HStack>
                  </VStack>
                  <Button
                    leftIcon={<FaDownload />}
                    bg="brand.ltRed"
                    color="white"
                    _hover={{ bg: 'brand.dkRed' }}
                    onClick={handleDownload}
                    size="sm"
                  >
                    Download
                  </Button>
                </HStack>
              </Box>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      <DisclaimerModal />
    </Box>
  );
};
